// src/components/ModifierEditModal.jsx
//
// Edit/delete existing modifier groups and options for a single dish.
// Creating brand new groups/options is deliberately out of scope for
// this pass -- see menu-data-template-spec.md, section 8.
//
// Each group and option row saves independently (no batched "Save All"),
// so a failure in one row never leaves the person wondering which of
// several changes actually went through.
//
// price_delta is stored in cents in the database (confirmed directly
// via api/menu.js's "// cents" comment and MenuDrawer.jsx's /100
// display conversion). This component keeps a separate dollar-display
// draft value per option, converting to/from cents only at save time.

import React, { useState } from 'react';
import { portalFetch } from '../lib/supabase';

const inputStyle = {
  width: '100%', padding: '0.5rem 0.7rem',
  border: '1.5px solid var(--border)', borderRadius: 6,
  fontSize: '0.85rem', outline: 'none',
  color: 'var(--ink)', background: 'white',
};

function centsToDollarDisplay(cents) {
  return ((cents || 0) / 100).toFixed(2);
}

function dollarDisplayToCents(display) {
  const parsed = parseFloat(display);
  if (Number.isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
}

export default function ModifierEditModal({ dish, onClose, onChanged }) {
  const [groups, setGroups] = useState(() =>
    (dish.modifier_groups || []).map(g => ({
      ...g,
      modifier_options: (g.modifier_options || []).map(o => ({
        ...o,
        price_delta_display: centsToDollarDisplay(o.price_delta),
      })),
    }))
  );
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // -- New group creation --
  const [showAddGroupForm, setShowAddGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupRequired, setNewGroupRequired] = useState(false);
  const [newGroupMin, setNewGroupMin] = useState(0);
  const [newGroupMax, setNewGroupMax] = useState(1);
  const [creatingGroup, setCreatingGroup] = useState(false);

  // -- New option creation -- only one group's "add option" form open
  // at a time, mirroring the same one-at-a-time editing pattern used
  // elsewhere in the portal (e.g. Menu.jsx's dish editing).
  const [addOptionForGroupId, setAddOptionForGroupId] = useState(null);
  const [newOptionName, setNewOptionName] = useState('');
  const [newOptionPrice, setNewOptionPrice] = useState('0.00');
  const [creatingOption, setCreatingOption] = useState(false);

  const updateGroupField = (groupId, field, value) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, [field]: value } : g));
  };

  const updateOptionField = (groupId, optionId, field, value) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      return {
        ...g,
        modifier_options: g.modifier_options.map(o =>
          o.id === optionId ? { ...o, [field]: value } : o
        ),
      };
    }));
  };

  const saveGroup = async (group) => {
    setSavingId(group.id);
    const result = await portalFetch('/modifiers', {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'group',
        id: group.id,
        updates: {
          name: group.name,
          required: group.required,
          min_selections: parseInt(group.min_selections, 10) || 0,
          max_selections: parseInt(group.max_selections, 10) || 0,
        },
      }),
    });
    setSavingId(null);
    if (result?.group) onChanged?.();
  };

  const saveOption = async (groupId, option) => {
    setSavingId(option.id);
    const result = await portalFetch('/modifiers', {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'option',
        id: option.id,
        updates: {
          name: option.name,
          price_delta: dollarDisplayToCents(option.price_delta_display),
        },
      }),
    });
    setSavingId(null);
    if (result?.option) onChanged?.();
  };

  const deleteGroup = async (group) => {
    if (!window.confirm(`Delete the "${group.name}" modifier group and all its options? This can't be undone.`)) return;
    setDeletingId(group.id);
    const result = await portalFetch('/modifiers', {
      method: 'DELETE',
      body: JSON.stringify({ type: 'group', id: group.id }),
    });
    setDeletingId(null);
    if (result?.ok) {
      setGroups(prev => prev.filter(g => g.id !== group.id));
      onChanged?.();
    }
  };

  const deleteOption = async (groupId, option) => {
    if (!window.confirm(`Delete "${option.name}"?`)) return;
    setDeletingId(option.id);
    const result = await portalFetch('/modifiers', {
      method: 'DELETE',
      body: JSON.stringify({ type: 'option', id: option.id }),
    });
    setDeletingId(null);
    if (result?.ok) {
      setGroups(prev => prev.map(g =>
        g.id === groupId
          ? { ...g, modifier_options: g.modifier_options.filter(o => o.id !== option.id) }
          : g
      ));
      onChanged?.();
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    const result = await portalFetch('/modifiers', {
      method: 'POST',
      body: JSON.stringify({
        type: 'group',
        dishId: dish.id,
        name: newGroupName.trim(),
        required: newGroupRequired,
        min_selections: parseInt(newGroupMin, 10) || 0,
        max_selections: parseInt(newGroupMax, 10) || 1,
      }),
    });
    setCreatingGroup(false);
    if (result?.group) {
      setGroups(prev => [...prev, { ...result.group, modifier_options: [] }]);
      setShowAddGroupForm(false);
      setNewGroupName('');
      setNewGroupRequired(false);
      setNewGroupMin(0);
      setNewGroupMax(1);
      onChanged?.();
    }
  };

  const handleCreateOption = async (groupId) => {
    if (!newOptionName.trim()) return;
    setCreatingOption(true);
    const result = await portalFetch('/modifiers', {
      method: 'POST',
      body: JSON.stringify({
        type: 'option',
        modifierGroupId: groupId,
        name: newOptionName.trim(),
        price_delta: dollarDisplayToCents(newOptionPrice),
      }),
    });
    setCreatingOption(false);
    if (result?.option) {
      setGroups(prev => prev.map(g =>
        g.id === groupId
          ? { ...g, modifier_options: [...g.modifier_options, { ...result.option, price_delta_display: centsToDollarDisplay(result.option.price_delta) }] }
          : g
      ));
      setAddOptionForGroupId(null);
      setNewOptionName('');
      setNewOptionPrice('0.00');
      onChanged?.();
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'white', borderRadius: 12, maxWidth: 560, width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Modifiers — {dish.name}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>

        {groups.map(group => (
          <div key={group.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0.6rem', alignItems: 'end', marginBottom: '0.7rem' }}>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.2rem' }}>Group name</label>
                <input style={inputStyle} value={group.name} onChange={e => updateGroupField(group.id, 'name', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.2rem' }}>Min</label>
                <input style={{ ...inputStyle, width: 60 }} type="number" value={group.min_selections ?? 0} onChange={e => updateGroupField(group.id, 'min_selections', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.2rem' }}>Max</label>
                <input style={{ ...inputStyle, width: 60 }} type="number" value={group.max_selections ?? 0} onChange={e => updateGroupField(group.id, 'max_selections', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!group.required} onChange={e => updateGroupField(group.id, 'required', e.target.checked)} style={{ accentColor: 'var(--orange)' }} />
                Required
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => saveGroup(group)}
                  disabled={savingId === group.id}
                  style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 6, padding: '0.35rem 0.8rem', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}
                >
                  {savingId === group.id ? 'Saving...' : 'Save group'}
                </button>
                <button
                  onClick={() => deleteGroup(group)}
                  disabled={deletingId === group.id}
                  style={{ background: 'none', border: '1px solid var(--border)', color: '#c0392b', borderRadius: 6, padding: '0.35rem 0.8rem', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}
                >
                  {deletingId === group.id ? 'Deleting...' : 'Delete group'}
                </button>
              </div>
            </div>

            {((group.modifier_options || []).length > 0 || addOptionForGroupId === group.id) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 65px 75px', gap: '0.5rem', padding: '0.3rem 0 0.1rem' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Option name</div>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Price ($)</div>
                <div />
                <div />
              </div>
            )}
            {(group.modifier_options || []).map(option => (
              <div key={option.id} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 65px 75px', gap: '0.5rem', alignItems: 'center', padding: '0.4rem 0', borderTop: '1px solid var(--warm)' }}>
                <input style={inputStyle} value={option.name} onChange={e => updateOptionField(group.id, option.id, 'name', e.target.value)} />
                <input
                  style={inputStyle}
                  type="number"
                  step="0.01"
                  value={option.price_delta_display}
                  onChange={e => updateOptionField(group.id, option.id, 'price_delta_display', e.target.value)}
                />
                <button
                  onClick={() => saveOption(group.id, option)}
                  disabled={savingId === option.id}
                  style={{ background: 'var(--warm)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.3rem 0.6rem', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  {savingId === option.id ? '...' : 'Save'}
                </button>
                <button
                  onClick={() => deleteOption(group.id, option)}
                  disabled={deletingId === option.id}
                  style={{ background: 'none', border: 'none', color: '#c0392b', fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  {deletingId === option.id ? '...' : 'Remove'}
                </button>
              </div>
            ))}

            {addOptionForGroupId === group.id ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 65px 75px', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0', borderTop: '1px solid var(--warm)' }}>
                <input style={inputStyle} placeholder="Option name" value={newOptionName} onChange={e => setNewOptionName(e.target.value)} />
                <input style={inputStyle} type="number" step="0.01" value={newOptionPrice} onChange={e => setNewOptionPrice(e.target.value)} />
                <button
                  onClick={() => handleCreateOption(group.id)}
                  disabled={creatingOption}
                  style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 6, padding: '0.3rem 0.6rem', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  {creatingOption ? '...' : 'Add'}
                </button>
                <button
                  onClick={() => { setAddOptionForGroupId(null); setNewOptionName(''); setNewOptionPrice('0.00'); }}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setAddOptionForGroupId(group.id); setNewOptionName(''); setNewOptionPrice('0.00'); }}
                style={{ background: 'none', border: 'none', color: 'var(--orange)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: '0.5rem 0 0.2rem', textAlign: 'left' }}
              >
                + Add option
              </button>
            )}
          </div>
        ))}

        {groups.length === 0 && !showAddGroupForm && (
          <div style={{ color: 'var(--muted)', fontSize: '0.875rem', textAlign: 'center', padding: '2rem 0' }}>
            This dish has no modifier groups yet.
          </div>
        )}

        {showAddGroupForm ? (
          <div style={{ border: '1px dashed var(--border)', borderRadius: 10, padding: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0.6rem', alignItems: 'end', marginBottom: '0.7rem' }}>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.2rem' }}>Group name</label>
                <input style={inputStyle} placeholder="e.g. Choose Your Spice Level" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.2rem' }}>Min</label>
                <input style={{ ...inputStyle, width: 60 }} type="number" value={newGroupMin} onChange={e => setNewGroupMin(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.2rem' }}>Max</label>
                <input style={{ ...inputStyle, width: 60 }} type="number" value={newGroupMax} onChange={e => setNewGroupMax(e.target.value)} />
              </div>
            </div>
            <p style={{ fontSize: '0.72rem', color: 'var(--muted)', lineHeight: 1.4, marginBottom: '0.7rem' }}>
              Tip: for an optional checklist (e.g. removable ingredients), set <strong>Min to 0</strong> and <strong>Max generously high</strong> (like 10) so diners can pick as many or as few as they want. A diner can never select more options than the group actually has, so a high Max is always safe.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={newGroupRequired} onChange={e => setNewGroupRequired(e.target.checked)} style={{ accentColor: 'var(--orange)' }} />
                Required
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleCreateGroup}
                  disabled={creatingGroup}
                  style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 6, padding: '0.35rem 0.8rem', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}
                >
                  {creatingGroup ? 'Creating...' : 'Create group'}
                </button>
                <button
                  onClick={() => { setShowAddGroupForm(false); setNewGroupName(''); }}
                  style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--ink)', borderRadius: 6, padding: '0.35rem 0.8rem', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddGroupForm(true)}
            style={{ width: '100%', background: 'none', border: '1px dashed var(--border)', color: 'var(--orange)', borderRadius: 10, padding: '0.8rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
          >
            + Add modifier group
          </button>
        )}
      </div>
    </div>
  );
}
